import React, { memo, useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { BiConversation } from "react-icons/bi";
import { MdOutlineSupportAgent, MdWeb } from "react-icons/md";
import { GrConnectivity } from "react-icons/gr";
import { FaComputer } from "react-icons/fa6";
import AbstractBackground from "../components/AbstractBackground";
const servicios = [{
  numero: "01",
  icon: BiConversation,
  titulo: "Asesoramiento para tu negocio",
  descripcion: "Te ayudamos a entender tus necesidades de software y trabajamos juntos para implementar las soluciones adecuadas."
}, {
  numero: "02",
  icon: FaComputer,
  titulo: "Desarrollo de software a medida",
  descripcion: "Creamos soluciones personalizadas para satisfacer las necesidades específicas de tu negocio con tecnología avanzada."
}, {
  numero: "03",
  icon: MdWeb,
  titulo: "Desarrollo de páginas web",
  descripcion: "Diseñamos y desarrollamos sitios web atractivos y funcionales para que tu negocio gane presencia online y atraiga más clientes."
}, {
  numero: "04",
  icon: GrConnectivity,
  titulo: "Integración con sistemas y APIs",
  descripcion: "Conectamos tu software con otras plataformas y servicios para mejorar su funcionalidad y automatizar procesos."
}, {
  numero: "05",
  icon: MdOutlineSupportAgent,
  titulo: "Mantenimiento y soporte",
  descripcion: "Brindamos soporte técnico y optimización continua para garantizar un sistema eficiente, seguro y sin interrupciones."
}];
const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.15
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <div ref={sectionRef} id="servicios" className="relative isolate overflow-hidden bg-[#0a0b0d] pt-32 pb-40">
            {/* Línea decorativa superior */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#bdff57]/30 to-transparent" />
            {/* Fondo negro con iluminaciones verdes */}
            <AbstractBackground variant="glows" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: "easeOut"
      }} className="text-center mb-20">
                    <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium text-[#bdff57] border border-[#bdff57]/30 rounded-full bg-[#bdff57]/5">
                        Lo que hacemos
                    </span>
                    <h2 className="text-4xl sm:text-6xl lg:text-7xl font-normal text-white tracking-tight">
                        <span className="text-[#bdff57]">Nuestros</span> servicios
                    </h2>
                    <p className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto">
                        Nuestros principales servicios incluyen
                    </p>
                </motion.div>

                {/* Grid único: 2 cols en tablet, 3+2 centradas en desktop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                    {servicios.slice(0, 3).map((servicio, index) => <ServiceCardMotion key={servicio.numero} servicio={servicio} index={index} isVisible={isVisible} />)}

                    {/* En tablet las 4 y 5 entran al grid padre (2-2-1); en lg quedan centradas abajo */}
                    <div className="contents lg:col-span-3 lg:flex lg:justify-center">
                        <div className="contents lg:grid lg:w-2/3 lg:grid-cols-2 lg:gap-6 lg:items-stretch">
                            {servicios.slice(3).map((servicio, index) => <ServiceCardMotion key={servicio.numero} servicio={servicio} index={index + 3} isVisible={isVisible} centerOnTablet={index === 1} />)}
                        </div>
                    </div>
                </div>

                {/* CTA sutil */}
                <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-16 text-center">
                    <p className="text-gray-500 text-sm">
                        ¿Necesitas algo más específico?{' '}
                        <a href="#contacto" className="text-[#bdff57] hover:underline">
                            Conversemos
                        </a>
                    </p>
                </motion.div>
            </div>
        </div>;
};
const ServiceCardMotion = memo(({
  servicio,
  index,
  isVisible,
  centerOnTablet = false
}) => <motion.div initial={{
  opacity: 0,
  y: 40,
  scale: 0.95
}} animate={isVisible ? {
  opacity: 1,
  y: 0,
  scale: 1
} : {}} transition={{
  duration: 0.45,
  ease: "easeOut",
  delay: 0.2 + index * 0.08
}} style={{
  willChange: "transform, opacity"
}} className={["group flex h-full min-h-0", centerOnTablet && "sm:max-lg:col-span-2 sm:max-lg:justify-self-center sm:max-lg:w-[calc((100%-1.5rem)/2)]"].filter(Boolean).join(" ")}>
        <ServiceCard servicio={servicio} />
    </motion.div>);
const ServiceCard = memo(({
  servicio
}) => <div className="relative flex h-full w-full flex-col p-8 rounded-2xl bg-gradient-to-br from-[#1a1b1e]/80 to-[#141517]/80 backdrop-blur-[2px] border border-white/5 hover:border-[#bdff57]/30 transition-[border-color,background-color,transform] duration-300 overflow-hidden">
        {/* Efecto de brillo en hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#bdff57]/0 to-[#bdff57]/0 group-hover:from-[#bdff57]/10 group-hover:to-transparent transition-all duration-300" />
        
        {/* Línea inferior en hover */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#bdff57]/0 via-[#bdff57] to-[#bdff57]/0 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
        
        {/* Número decorativo */}
        <div className="absolute -top-3 -right-3 w-20 h-20 bg-[#bdff57] rounded-full flex items-end justify-start p-6 opacity-90">
            <span className="text-black font-bold text-xl">{servicio.numero}</span>
        </div>

        {/* Contenido */}
        <div className="relative z-10">
            <div className="w-14 h-14 rounded-xl bg-[#bdff57]/10 flex items-center justify-center mb-6 group-hover:bg-[#bdff57]/20 transition-colors duration-200">
                <servicio.icon className="w-7 h-7 text-[#bdff57]" />
            </div>
            
            <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-[#bdff57] transition-colors duration-200 pr-12 min-h-[3.5rem]">
                {servicio.titulo}
            </h3>
            
            <p className="flex-1 text-gray-400 leading-relaxed text-sm">
                {servicio.descripcion}
            </p>
        </div>
    </div>);
export default Services;